import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HealthMetric, getStatusColor } from "@/utils/deviceHealth";
import { AlertTriangle, CheckCircle, Info, XCircle, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const SCORING_TOOLTIPS: Record<string, string> = {
  "OS Compliance": "Score starts at 100 for the latest OS. Deducts 15 points per version behind. An extra 15-point penalty applies if the current version has actively exploited CVEs.",
  "Security Patches": "Based on days since last check-in. Full score within 7 days; deducts 20 pts after 7 days, 30 more after 14, and 40 more after 30 days.",
  "Encryption": "Full score (100) when device encryption (FileVault) is active. Reduced to 50 if the device is in a pending enrollment state.",
  "Battery Health": "Scored by battery level: 80%+ = 100 (excellent), 50-79% = 85 (good), 20-49% = 70 (fair), below 20% = 40 (poor). Unknown level scores 50.",
  "App Compliance": "Full score when managed apps are installed. Drops to 30 if no apps are found on an enrolled device.",
};

interface HealthMetricCardProps {
  metric: HealthMetric;
  showRecommendations?: boolean;
}

export const HealthMetricCard = ({ 
  metric, 
  showRecommendations = true 
}: HealthMetricCardProps) => {
  const statusColor = getStatusColor(metric.status);
  
  const getStatusIcon = () => {
    switch (metric.status) {
      case "excellent":
        return <CheckCircle className="h-4 w-4 text-success" />;
      case "good":
        return <CheckCircle className="h-4 w-4 text-info" />;
      case "fair":
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case "poor":
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };
  
  const getStatusVariant = () => {
    switch (metric.status) {
      case "excellent":
        return "default";
      case "good":
        return "secondary";
      case "fair":
        return "outline";
      case "poor":
        return "destructive";
      default:
        return "outline";
    }
  };

  const tooltipText = SCORING_TOOLTIPS[metric.name];

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader className="pb-3">
        <GlassCardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            {metric.name}
            {tooltipText && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    {tooltipText}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <Badge variant={getStatusVariant()}>
            {metric.status}
          </Badge>
        </GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Score</span>
            <span className="font-medium">{metric.score}/{metric.maxScore}</span>
          </div>
          <Progress 
            value={(metric.score / metric.maxScore) * 100} 
            className="h-2"
            style={{
              // @ts-ignore
              '--progress-color': statusColor
            } as React.CSSProperties}
          />
        </div>
        
        <p className="text-sm text-muted-foreground">
          {metric.description}
        </p>
        
        {showRecommendations && metric.recommendations && metric.recommendations.length > 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside text-sm space-y-1">
                {metric.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </GlassCardContent>
    </GlassCard>
  );
};